import React, { useRef, useState, useContext } from "react";
import Button from "@mui/material/Button";
import SignatureInput from "../components/SignatureInput";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import dayjs from "dayjs";
import DateInput from "../components/DateInput";
import TextInput from "../components/TextInput";
import CheckboxInput from "../components/CheckboxInput";
import AlertFeedback from "../components/AlertFeedback";
import { getPDF, resetForm } from "./utils";
import styled from "styled-components";
import { uploadWaiver } from "./UploadService";
import { FirebaseAppContext } from "../context/FirebaseAppProvider";
import { getFirestore, collection, addDoc } from "firebase/firestore";

const InputGroup = styled.div`
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 20px;
  margin-bottom: 20px;
`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  gap: 40px;
  padding-top: 20px;
  padding-bottom: 20px;
`;

const Section = styled.div`
  margin-top: 20px;
  margin-bottom: 20px;
`;

export function Waiver() {
  const firebaseApp = useContext(FirebaseAppContext);
  const db = getFirestore(firebaseApp);

  const [isUploading, setIsUploading] = useState(false);

  // Participant Info
  const [name, setName] = useState("");
  const [birthday, setBirthday] = useState(dayjs());
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const signature = useRef(null);
  const [negligentCheck, setNegligentCheck] = useState(false);

  // Guardian Info
  const [nameGuardian, setNameGuardian] = useState("");
  const [emailGuardian, setEmailGuardian] = useState("");
  const signatureGuardian = useRef(null);

  // Alert Feedback
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("warning");

  const isBirthdayOver18 = () => {
    const today = dayjs();
    const age = today.diff(birthday, "year");
    return age >= 18;
  };

  const isBirthdayUnder18 = () => {
    const today = dayjs();
    const age = today.diff(birthday, "year");
    return age < 18;
  };

  const isSignatureFilled = () => {
    const svg = signature?.current?.svg;

    if (!svg) {
      return false;
    }

    const allPathsEmpty = Array.from(svg.querySelectorAll("path")).every(
      (path) => !path.hasAttribute("d") || path.getAttribute("d")?.trim() === ""
    );

    if (allPathsEmpty) {
      return false;
    } else {
      return true;
    }
  };

  const isSignatureGuardianFilled = () => {
    const svg = signatureGuardian?.current?.svg;

    if (!svg) {
      return false;
    }

    const allPathsEmpty = Array.from(svg.querySelectorAll("path")).every(
      (path) => !path.hasAttribute("d") || path.getAttribute("d")?.trim() === ""
    );

    if (allPathsEmpty) {
      return false;
    } else {
      return true;
    }
  };

  const isFormValid = () => {
    const isNameValid = !!name;
    const isBirthdayValid = !!birthday;
    const isPhoneValid = !!phone;
    const isEmailValid = !!email;
    const isParentGuardianNameValid = isBirthdayUnder18()
      ? !!nameGuardian
      : true;
    const isParentGuardianEmailValid = isBirthdayUnder18()
      ? !!emailGuardian
      : true;
    const isNegligentCheckValid = isBirthdayOver18() ? !!negligentCheck : true;
    const isSignatureValid = isBirthdayOver18() ? isSignatureFilled() : true;
    const isSignatureGuardianValid = isBirthdayUnder18()
      ? isSignatureGuardianFilled()
      : true;

    return (
      isNameValid &&
      isBirthdayValid &&
      isPhoneValid &&
      isEmailValid &&
      isParentGuardianNameValid &&
      isParentGuardianEmailValid &&
      isNegligentCheckValid &&
      isSignatureValid &&
      isSignatureGuardianValid
    );
  };

  const onClickAgreeAndSign = async () => {
    if (isFormValid()) {
      setIsUploading(true);
      const waiverParams = {
        name,
        birthday,
        phone,
        email,
        signature,
        negligentCheck,
        nameGuardian,
        emailGuardian,
        signatureGuardian,
      };
      const waiver = await getPDF(waiverParams);
      const { downloadURL, fullPath } = await uploadWaiver(waiver);
      // Creating this document triggers the emailSignedWaiver cloud function.
      await addDoc(collection(db, `waivers`), {
        name: waiverParams.name,
        email: waiverParams.email,
        emailGuardian: isBirthdayUnder18() ? waiverParams.emailGuardian : null,
        waiver: downloadURL,
        waiverPath: fullPath,
        signedAt: new Date(),
      });
      setAlertSeverity("success");
    } else {
      setAlertSeverity("warning");
    }
    setAlertOpen(true);
  };

  const getListOfInvalidFields = () => {
    const isNameValid = !!name;
    const isBirthdayValid = !!birthday;
    const isPhoneValid = !!phone;
    const isEmailValid = !!email;
    const isParentGuardianNameValid = isBirthdayUnder18()
      ? !!nameGuardian
      : true;
    const isParentGuardianEmailValid = isBirthdayUnder18()
      ? !!emailGuardian
      : true;
    const isNegligentCheckValid = isBirthdayOver18() ? !!negligentCheck : true;
    const isSignatureValid = isBirthdayOver18() ? isSignatureFilled() : true;
    const isSignatureGuardianValid = isBirthdayUnder18()
      ? isSignatureGuardianFilled()
      : true;

    const invalidFields = [];
    if (!isNameValid) invalidFields.push("Participant Name");
    if (!isBirthdayValid) invalidFields.push("Birthday");
    if (!isPhoneValid) invalidFields.push("Phone Number");
    if (!isEmailValid) invalidFields.push("Email");
    if (!isParentGuardianNameValid) invalidFields.push("Parent/Guardian Name");
    if (!isParentGuardianEmailValid)
      invalidFields.push("Parent/Guardian Email");
    if (!isNegligentCheckValid) invalidFields.push("Negligent Check");
    if (!isSignatureValid) invalidFields.push("Participant Signature");
    if (!isSignatureGuardianValid)
      invalidFields.push("Parent/Guardian Signature");
    return invalidFields;
  };

  return (
    <Container style={{ backgroundColor: "#F0F0F0" }}>
      <Form>
        {/* Form Title */}
        <Typography variant="h4">
          Assumption of Risk and Waiver of Claims and Liabilities
        </Typography>
        <hr width="100%" />
        <Typography variant="h5">
          Alberta Whitewater Association - ASSUMPTION OF RISK AND WAIVER OF
          CLAIMS AND LIABILITY AGREEMENT
        </Typography>
        <hr width="100%" />

        {/* Participant Info */}
        <div>
          <Typography variant="h6">Participant Information</Typography>
          <InputGroup>
            <TextInput
              label={"Participant Name"}
              text={name}
              setText={setName}
              required={true}
            />
            <DateInput
              label={"Birthday"}
              date={birthday}
              setDate={setBirthday}
            />
            <TextInput
              label={"Phone Number"}
              text={phone}
              setText={setPhone}
              required={true}
            />
            <TextInput
              label={"Email"}
              text={email}
              setText={setEmail}
              required={true}
            />
          </InputGroup>
        </div>

        {/* Guardian Info */}
        {isBirthdayUnder18() && (
          <div>
            <Typography variant="h6" sx={{ marginBottom: "-5px" }}>
              Guardian Information
            </Typography>
            <Typography variant="caption">
              If the Participant is younger than 18 years old
            </Typography>
            <InputGroup>
              <TextInput
                label={"Parent/Guardian Name"}
                text={nameGuardian}
                setText={setNameGuardian}
              />
              <TextInput
                label={"Email"}
                text={emailGuardian}
                setText={setEmailGuardian}
              />
            </InputGroup>
          </div>
        )}

        {/* Agreement Section 1 */}
        <Section>
          <Typography variant="body2" style={{ textAlign: "start" }}>
            This Agreement must be signed by the Participant and/or the
            Participant’s parent/guardian (if applicable, when the Participant
            is younger than 18 years old) prior to participation. The
            Participant acknowledges and agrees to the terms outlined in this
            document. When applicable, the Participant’s parent/guardian
            acknowledges and agrees to the terms on behalf of the Participant.
          </Typography>

          <br />

          <Typography variant="body2" style={{ textAlign: "start" }}>
            <strong>ACTIVITIES</strong>
            <br /> The Participant is voluntarily participating in canoeing
            and/or kayaking and the spectating, orientation, instruction,
            activities, competitions, programs, and services (collectively the
            “Activities”) of the Alberta Whitewater Association. The Activities
            may include but are not limited to including lessons, instructional
            sessions, river runs, competitions, races, practice runs, training,
            personal or strength training, dry land training, training using
            machines or weights, and other aerobic and anaerobic activities. The
            Alberta Whitewater Association, its clubs, and its affiliated
            discipline associations and their respective Directors, Officers,
            committee members, members, employees, coaches, volunteers,
            officials, participants, agents, sponsors, owners/operators of the
            facilities/courses in which the Activities take place, and
            representatives (collectively the “Organization”) are not
            responsible for any injury, personal injury, damage, property
            damage, expense, loss of income or loss of any kind suffered by a
            Participant during or as a result of the Activities and/or, when the
            Participant is 18 years old or older, when caused by the negligence
            of the Organization.
          </Typography>

          <br />

          <Typography variant="body2" style={{ textAlign: "start" }}>
            <strong>RISKS</strong>
            <br />
            The Activities have foreseeable and unforeseeable inherent risks,
            hazards and dangers that no amount of care, caution or expertise can
            eliminate. These inherent physical risks include:
            <br />
            a) <strong>Health:</strong> executing physical techniques; physical
            exertion; stretching; dehydration; fatigue; hypothermia or
            hyperthermia; cardiovascular workouts; rapid movements and stops;
            lack of fitness or conditioning; traumatic injury; sprains and
            fractures, spinal cord injuries, bacterial infections; rashes; and
            the transmission of communicable diseases, including viruses of all
            kinds
            <br />
            b) <strong>Premises:</strong> drowning; falls; collisions with
            objects or barriers that are a part of the premises; dangerous,
            unsafe, or irregular conditions on water, ground, or other surfaces;
            temperature of the water; interactions with animals and plants;
            extreme weather conditions and hazards such as thunderstorms and
            lightning; and travel to and from the premises
            <br />
            c) <strong>Use of equipment:</strong> mechanical failure of the
            equipment; negligent design or manufacture of the equipment; the
            provision of, or the failure by, the Organization to provide any
            warnings, directions, instructions or guidance as to the use of the
            equipment; failure to wear safety or protective equipment; and
            failure to use or operate equipment within the Participant’s own
            ability
            <br />
            d) <strong>Contact:</strong> contact with participants, kayaks,
            canoes, paddles, vehicles, plants or animals, or other persons; and
            other contact that may lead to serious bodily injury, including but
            not limited to concussions and/or other brain injury or serious
            spinal injury
            <br />
            e) <strong>Advice:</strong> negligent advice regarding the
            Activities
            <br />
            f) <strong>Ability:</strong> failing to act safely or within the
            Participant’s own ability or within designated areas
            <br />
            g) <strong>Sport:</strong> canoeing and kayaking, swimming, swift
            water rescues, camping and their inherent risks, including becoming
            lost or disoriented during participation
            <br />
            h) <strong>Conduct:</strong> the Participant’s conduct and conduct
            of other persons including any physical altercation between
            participants
            <br />
            i) <strong>Travel:</strong> travel to and from the Activities
          </Typography>

          <br />

          <Typography variant="body2" style={{ textAlign: "start" }}>
            Negligence. The Organization may be negligent, which may include
            failure by the Organization to take reasonable steps to safeguard or
            protect the Participant from the risks, dangers and hazards
            associated with participation in the Activities.
          </Typography>
        </Section>

        {/* Negligent Check */}
        <CheckboxInput
          label="The Participant accepts that the Organization may be negligent (check only if the Participant is 18 years old or older)"
          checked={negligentCheck}
          setChecked={setNegligentCheck}
        />

        {/* Agreement Section 2 */}
        <Section>
          <Typography variant="body2" style={{ textAlign: "start" }}>
            <strong>TERMS</strong>
            <br />
            In consideration of the Organization allowing the Participant to
            participate in the Activities, the Participant agrees:
            <br />
            <br />
            a) That the Participant is not relying on any oral or written
            statements made by the Organization or their agents
            <br />
            b) That the Participant’s mental and physical condition is
            appropriate to participate in the Activities
            <br />
            c) To comply with the rules and regulations for participation in the
            Activities
            <br />
            d) To comply with the rules of the facility, course or equipment as
            well as any directions or instructions from the Organization
            <br />
            e) That if the Participant observes an unusual significant hazard or
            risk, the Participant will remove themselves from participation
            <br />
            f) That the Organization may secure transport to emergency medical
            services as it deems necessary for the Participant’s immediate
            health and safety, and that the Participant shall be financially
            responsible for such services
            <br />
            g) The risks associated with the Activities are increased when the
            Participant is impaired and the Participant will not participate if
            impaired in any way
            <br />
            h) That it is the Participant’s (or the Participant’s
            parent/guardian, if applicable) sole responsibility to assess
            whether any Activities are too difficult for the Participant. By the
            Participant commencing an Activity, the Participant (or the
            Participant’s parent/guardian, if applicable) acknowledges and
            accepts the suitability and conditions of the Activity
            <br />
            i) That the Participant (or the Participant’s parent/guardian, if
            applicable) is responsible for choosing the Participant’s safety or
            protective equipment and the secure fitting of that equipment
          </Typography>

          <br />

          <Typography variant="body2" style={{ textAlign: "start" }}>
            <strong>ACTIVITIES STATUS</strong>
            <br />
            The Participant (and the Participant’s parent/guardian, if
            applicable) agrees that the Organization has the discretion to
            cancel or modify the structure of any Activities due to a public
            health or safety issue.
            <br />
            The Participant (and the Participant’s parent/guardian, if
            applicable) agrees that the Organization may implement and enforce
            guidelines for participation that may include requiring that
            personal protective equipment be worn by Participants (and their
            parents/guardians, if applicable). When required, the Participant is
            responsible for providing their own personal protective equipment.
          </Typography>

          <br />

          <Typography variant="body2" style={{ textAlign: "start" }}>
            <strong>IMAGE CONSENT</strong>
            <br />
            The Participant (and the Participant’s parent/guardian, if
            applicable) grants permission to the Organization, without payment
            of any fee, to photograph and/or record the Participant’s image
            and/or voice and to use this material to promote the sport, the
            Organization, and/or the Organization’s Activities through social
            media, newsletters, websites, television, film, radio, print and/or
            displays. The Participant (or the Participant’s parent/guardian, if
            applicable) understands that they waive any claim to remuneration
            for use of audio/visual materials used for these purposes.
          </Typography>

          <br />

          <Typography variant="body2" style={{ textAlign: "start" }}>
            <strong>DISCLAIMER</strong>
            <br />
            <u>When the Participant is 18 years old or older</u>, the
            Participant assumes all risks arising out of, associated with or
            related to, participation in the Activities and waives any and all
            claims that the Participant may have now or in the future against
            the Organization. The Participant, <u>when 18 years old or older</u>
            , accepts and fully assumes all such risks and possibility of
            personal injury, death, property damage, expense, and related loss,
            including loss of income, resulting from participation in the
            Activities. The Participant forever indemnifies and releases the
            Organization from any and all liability for any and all claims,
            demands, actions, damages (including direct, indirect, special
            and/or consequential), losses, actions, judgments, and costs
            (including legal fees) (collectively, the “Claims”) which the
            Participant has or may have in the future, that might arise out of,
            result from, or relate to, participation in the Activities, even
            though such Claims may have been caused by any manner whatsoever,
            including but not limited to, the Organization’s negligence, gross
            negligence, negligent rescue, omissions, carelessness, breach of
            contract and/or breach of any statutory duty of care of the
            Organization.
            <br />
            <br />
            <u>For all Participants.</u> The Organization is not responsible or
            liable for any damage to the Participant’s vehicle, property, or
            equipment that may occur as a result of the Activities. This
            Agreement is intended to be as broad and inclusive as is permitted
            by law of the Province of Alberta and if any portion thereof is held
            invalid, the balance shall continue in full legal force and effect.
            The Participant (or the Participant’s parent/guardian, if
            applicable) agrees to file any lawsuit against the Organization in
            the Province of Alberta and further agrees that the substantive law
            of the Province of Alberta will apply with regard to conflict of law
            rules.
          </Typography>

          <br />

          <Typography variant="body2" style={{ textAlign: "start" }}>
            <strong>ACKNOWLEDGMENT</strong>
            <br />
            The Participant (and the Participant’s parent/guardian, if
            applicable) acknowledges that they have read and understand this
            Agreement, that they have executed this Agreement voluntarily, and
            that this Agreement is to be binding upon themselves, their heirs,
            spouse, children, parents, guardians, next of kin, executors,
            administrators and legal or personal representatives. They further
            acknowledge that by signing this Agreement they have waived their
            right to maintain a lawsuit against the Organization on the basis of
            any claims from which they have released herein.
          </Typography>
        </Section>

        {/* Signatures */}
        {isBirthdayOver18() && (
          <SignatureInput
            ref={signature}
            label={
              "Participant Signature (if the Participant is 18 years old or older)"
            }
          />
        )}

        {isBirthdayUnder18() && (
          <SignatureInput
            ref={signatureGuardian}
            label={
              "Parent/Guardian Signature (if the Participant is younger than 18 years old)"
            }
          />
        )}

        {/* Agree and Sign Button */}
        <Button
          variant="contained"
          color="success"
          sx={{ maxWidth: "200px", alignSelf: "center" }}
          onClick={onClickAgreeAndSign}
          disabled={isUploading}
        >
          Agree and Sign
        </Button>
      </Form>

      {/* Alert Feedback */}
      <AlertFeedback
        open={alertOpen}
        setOpen={setAlertOpen}
        severity={alertSeverity}
        successCallback={resetForm}
        missingFields={getListOfInvalidFields()}
      />
    </Container>
  );
}
